import { useState, useEffect } from 'react';
import axios from 'axios';
import AdminLayout from '../../components/layout/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { 
  Plus, Pencil, Trash2, Search, Users, Loader2, KeyRound, Eye, EyeOff, Building2, X
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_CONFIG = {
  actif: { label: 'Actif', className: 'bg-emerald-600 text-white hover:bg-emerald-600' },
  en_sommeil: { label: 'En sommeil', className: 'bg-amber-500 text-white hover:bg-amber-500' },
  inactif: { label: 'Inactif', className: 'bg-gray-400 text-white hover:bg-gray-400' },
};

const Clients = () => {
  const [clients, setClients] = useState([]);
  const [labs, setLabs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [labDialogOpen, setLabDialogOpen] = useState(false);
  const [labClient, setLabClient] = useState(null);
  const [selectedLabId, setSelectedLabId] = useState('');
  const [savingLab, setSavingLab] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCity, setFilterCity] = useState('');
  const [filterCountry, setFilterCountry] = useState('');
  const [filterStructure, setFilterStructure] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [passwordClient, setPasswordClient] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showFormPassword, setShowFormPassword] = useState(false);
  const [formData, setFormData] = useState({
    client_name: '', contact_email: '', telephone: '', notes: '', password: '',
    city: '', country: '', first_name: '', last_name: '', structure: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [showChangeMode, setShowChangeMode] = useState(false);
  const [showCurrentPwd, setShowCurrentPwd] = useState(false);

  useEffect(() => { fetchClients(); fetchLabs(); }, []);

  const fetchLabs = async () => {
    try {
      const res = await axios.get(`${API}/labs`);
      setLabs(res.data || []);
    } catch (e) {
      if (process.env.NODE_ENV !== 'production') console.debug('[Clients]', e?.message);
    }
  };

  const openLabDialog = (client) => {
    setLabClient(client);
    setSelectedLabId('');
    setLabDialogOpen(true);
  };

  const handleAssignLab = async () => {
    if (!labClient || !selectedLabId) return;
    setSavingLab(true);
    try {
      await axios.post(`${API}/channels`, {
        client_id: labClient.id,
        lab_id: selectedLabId,
      });
      toast.success('Laboratoire ajouté');
      setSelectedLabId('');
      // Refresh the modal data: re-fetch clients and update labClient in place
      const r = await axios.get(`${API}/clients`);
      setClients(r.data);
      const fresh = r.data.find(c => c.id === labClient.id);
      if (fresh) setLabClient(fresh);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de l'assignation");
    } finally {
      setSavingLab(false);
    }
  };

  const handleRemoveLab = async (labId) => {
    if (!labClient) return;
    if (!window.confirm('Retirer ce laboratoire de ce client ? L\'historique des messages sera conservé.')) return;
    // Find the channel id for this client+lab pair via the API
    try {
      // Need channel_id — fetch and find. Use admin /channels
      const ch = await axios.get(`${API}/channels`);
      const target = (ch.data || []).find(c => c.client_id === labClient.id && c.lab_id === labId && c.active);
      if (!target) {
        toast.error('Canal introuvable');
        return;
      }
      await axios.delete(`${API}/channels/${target.id}`);
      toast.success('Laboratoire retiré');
      const r = await axios.get(`${API}/clients`);
      setClients(r.data);
      const fresh = r.data.find(c => c.id === labClient.id);
      if (fresh) setLabClient(fresh);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
  };

  const fetchClients = async () => {
    try {
      const response = await axios.get(`${API}/clients`);
      setClients(response.data);
    } catch (error) {
      toast.error('Erreur de chargement des clients');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (client = null) => {
    if (client) {
      setEditingClient(client);
      setFormData({
        client_name: client.client_name,
        contact_email: client.contact_email,
        telephone: client.telephone || '',
        notes: client.notes || '',
        password: '',
        city: client.city || '',
        country: client.country || '',
        first_name: client.first_name || '',
        last_name: client.last_name || '',
        structure: client.structure || ''
      });
    } else {
      setEditingClient(null);
      setFormData({ client_name: '', contact_email: '', telephone: '', notes: '', password: '', city: '', country: '', first_name: '', last_name: '', structure: '' });
    }
    setShowFormPassword(false);
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (editingClient) {
        const { password, ...updateData } = formData;
        await axios.put(`${API}/clients/${editingClient.id}`, updateData);
        toast.success('Client mis à jour');
      } else {
        await axios.post(`${API}/clients`, formData);
        toast.success('Client créé');
      }
      setDialogOpen(false);
      fetchClients();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'enregistrement");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (client) => {
    if (!window.confirm(`Supprimer définitivement "${client.client_name}" ? Cette action est irréversible.`)) return;
    try {
      await axios.delete(`${API}/clients/${client.id}`);
      toast.success('Client supprimé');
      fetchClients();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleStatusChange = async (client, newStatus) => {
    try {
      await axios.put(`${API}/clients/${client.id}/status?status=${newStatus}`);
      toast.success(`Statut mis à jour : ${STATUS_CONFIG[newStatus].label}`);
      fetchClients();
    } catch (error) {
      toast.error('Erreur lors du changement de statut');
    }
  };

  const openPasswordDialog = async (client) => {
    setPasswordClient(client);
    setNewPassword('');
    setShowPassword(false);
    setShowChangeMode(false);
    setShowCurrentPwd(false);
    setCurrentPassword('');
    setPasswordDialogOpen(true);
    try {
      const r = await axios.get(`${API}/admin/credentials/client/${client.id}`);
      setCurrentPassword(r.data?.password || '');
    } catch {
      setCurrentPassword('');
    }
  };

  const handleSavePassword = async () => {
    if (!newPassword.trim()) {
      toast.error('Veuillez entrer un mot de passe');
      return;
    }
    setSavingPassword(true);
    try {
      await axios.post(`${API}/clients/${passwordClient.id}/reset-password`, { password: newPassword });
      toast.success('Mot de passe modifié');
      setCurrentPassword(newPassword);
      setNewPassword('');
      setShowChangeMode(false);
    } catch {
      toast.error('Erreur lors de la modification');
    } finally {
      setSavingPassword(false);
    }
  };

  // Build unique lists for filters
  const cities = [...new Set(clients.map(c => c.city).filter(Boolean))].sort();
  const countries = [...new Set(clients.map(c => c.country).filter(Boolean))].sort();
  const structures = [...new Set(clients.map(c => c.structure).filter(Boolean))].sort();

  const filteredClients = clients.filter(client => {
    const term = searchTerm.toLowerCase();
    const matchesSearch = !term ||
      (client.client_name || '').toLowerCase().includes(term) ||
      (client.contact_email || '').toLowerCase().includes(term) ||
      (client.first_name || '').toLowerCase().includes(term) ||
      (client.last_name || '').toLowerCase().includes(term) ||
      (client.structure || '').toLowerCase().includes(term);
    const matchesCity = !filterCity || client.city === filterCity;
    const matchesCountry = !filterCountry || client.country === filterCountry;
    const matchesStructure = !filterStructure || client.structure === filterStructure;
    return matchesSearch && matchesCity && matchesCountry && matchesStructure;
  });

  return (
    <AdminLayout title="Clients" subtitle="Gestion des cabinets dentaires">
      <Card className="border-border" data-testid="clients-page">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
          <CardTitle className="text-lg font-semibold flex items-center gap-2">
            <Users className="h-5 w-5" />
            Liste des clients ({filteredClients.length})
          </CardTitle>
          <Button onClick={() => handleOpenDialog()} data-testid="add-client-button">
            <Plus className="h-4 w-4 mr-2" />
            Ajouter
          </Button>
        </CardHeader>
        <CardContent>
          {/* Search & Filters */}
          <div className="flex flex-col sm:flex-row gap-3 mb-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Rechercher (nom, prénom, structure, email)..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-clients-input"
              />
            </div>
            {structures.length > 0 && (
              <Select value={filterStructure} onValueChange={(v) => setFilterStructure(v === 'all' ? '' : v)}>
                <SelectTrigger className="w-[180px]" data-testid="filter-structure">
                  <SelectValue placeholder="Structure" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toutes les structures</SelectItem>
                  {structures.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
            {cities.length > 0 && (
              <Select value={filterCity} onValueChange={(v) => setFilterCity(v === 'all' ? '' : v)}>
                <SelectTrigger className="w-[160px]" data-testid="filter-city">
                  <SelectValue placeholder="Ville" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toutes les villes</SelectItem>
                  {cities.map(city => <SelectItem key={city} value={city}>{city}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
            {countries.length > 0 && (
              <Select value={filterCountry} onValueChange={(v) => setFilterCountry(v === 'all' ? '' : v)}>
                <SelectTrigger className="w-[160px]" data-testid="filter-country">
                  <SelectValue placeholder="Pays" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les pays</SelectItem>
                  {countries.map(country => <SelectItem key={country} value={country}>{country}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
          </div>

          {/* Table */}
          {loading ? (
            <div className="text-center py-8">
              <Loader2 className="h-6 w-6 animate-spin mx-auto text-muted-foreground" />
            </div>
          ) : filteredClients.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              {searchTerm || filterCity || filterCountry ? 'Aucun résultat' : 'Aucun client enregistré'}
            </div>
          ) : (
            <div className="rounded-lg border border-border overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50">
                    <TableHead>Cabinet</TableHead>
                    <TableHead>Prénom / Nom</TableHead>
                    <TableHead>Structure</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Ville</TableHead>
                    <TableHead>Laboratoire</TableHead>
                    <TableHead>Statut</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredClients.map((client) => {
                    const statusKey = client.status || (client.active ? 'actif' : 'inactif');
                    const statusConf = STATUS_CONFIG[statusKey] || STATUS_CONFIG.inactif;
                    const fullName = [client.first_name, client.last_name].filter(Boolean).join(' ');
                    return (
                      <TableRow key={client.id} data-testid={`client-row-${client.id}`}>
                        <TableCell className="font-medium">{client.client_name}</TableCell>
                        <TableCell className="text-sm">{fullName || '-'}</TableCell>
                        <TableCell className="text-sm">{client.structure || '-'}</TableCell>
                        <TableCell className="text-muted-foreground text-sm">{client.contact_email}</TableCell>
                        <TableCell className="text-muted-foreground text-sm">{client.city || '-'}</TableCell>
                        <TableCell>
                          {client.labs && client.labs.length > 0 ? (
                            <div className="flex flex-wrap gap-1">
                              {client.labs.map(l => (
                                <Badge key={l.id} variant="outline" className="status-linked">{l.lab_name}</Badge>
                              ))}
                            </div>
                          ) : (
                            <span className="text-muted-foreground text-sm">Non assigné</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <Select value={statusKey} onValueChange={(v) => handleStatusChange(client, v)}>
                            <SelectTrigger className="h-7 w-[120px] border-0 p-0" data-testid={`status-select-${client.id}`}>
                              <Badge className={statusConf.className}>{statusConf.label}</Badge>
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="actif">Actif</SelectItem>
                              <SelectItem value="en_sommeil">En sommeil</SelectItem>
                              <SelectItem value="inactif">Inactif</SelectItem>
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            <Button variant="ghost" size="icon" onClick={() => openLabDialog(client)} title="Gérer les laboratoires" data-testid={`assign-lab-${client.id}`}>
                              <Building2 className="h-4 w-4" />
                            </Button>
                            <Button variant="ghost" size="icon" onClick={() => openPasswordDialog(client)} title="Modifier le mot de passe" data-testid={`password-btn-${client.id}`}>
                              <KeyRound className="h-4 w-4" />
                            </Button>
                            <Button variant="ghost" size="icon" onClick={() => handleOpenDialog(client)} title="Modifier" data-testid={`edit-client-${client.id}`}>
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button variant="ghost" size="icon" onClick={() => handleDelete(client)} className="text-destructive hover:text-destructive" title="Supprimer" data-testid={`delete-client-${client.id}`}>
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md" data-testid="client-dialog">
          <DialogHeader>
            <DialogTitle>{editingClient ? 'Modifier le client' : 'Nouveau client'}</DialogTitle>
            <DialogDescription>
              {editingClient ? 'Modifiez les informations du client' : 'Ajoutez un nouveau cabinet dentaire'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="client_name">Nom du cabinet *</Label>
                <Input id="client_name" value={formData.client_name} onChange={(e) => setFormData({ ...formData, client_name: e.target.value })} placeholder="Cabinet Dentaire ABC" required data-testid="client-name-input" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="contact_email">Email du client *</Label>
                <Input id="contact_email" type="email" value={formData.contact_email} onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })} placeholder="contact@cabinet.com" required disabled={!!editingClient} data-testid="client-email-input" />
              </div>
              {!editingClient && (
                <div className="space-y-2">
                  <Label htmlFor="password">Mot de passe *</Label>
                  <div className="relative">
                    <Input id="password" type={showFormPassword ? "text" : "password"} value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })} placeholder="Choisissez un mot de passe" required className="pr-10" data-testid="client-password-input" />
                    <Button type="button" variant="ghost" size="icon" className="absolute right-0 top-0 h-full px-3 hover:bg-transparent" onClick={() => setShowFormPassword(!showFormPassword)}>
                      {showFormPassword ? <EyeOff className="h-4 w-4 text-muted-foreground" /> : <Eye className="h-4 w-4 text-muted-foreground" />}
                    </Button>
                  </div>
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="first_name">Prénom</Label>
                  <Input id="first_name" value={formData.first_name} onChange={(e) => setFormData({ ...formData, first_name: e.target.value })} placeholder="Jean" data-testid="client-firstname-input" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="last_name">Nom</Label>
                  <Input id="last_name" value={formData.last_name} onChange={(e) => setFormData({ ...formData, last_name: e.target.value })} placeholder="Dupont" data-testid="client-lastname-input" />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="structure">Structure d'exercice</Label>
                <Input id="structure" value={formData.structure} onChange={(e) => setFormData({ ...formData, structure: e.target.value })} placeholder="SELARL Cabinet ABC, Centre dentaire X..." data-testid="client-structure-input" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="city">Ville</Label>
                  <Input id="city" value={formData.city} onChange={(e) => setFormData({ ...formData, city: e.target.value })} placeholder="Paris" data-testid="client-city-input" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="country">Pays</Label>
                  <Input id="country" value={formData.country} onChange={(e) => setFormData({ ...formData, country: e.target.value })} placeholder="France" data-testid="client-country-input" />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="telephone">Téléphone</Label>
                <Input id="telephone" value={formData.telephone} onChange={(e) => setFormData({ ...formData, telephone: e.target.value })} placeholder="01 23 45 67 89" data-testid="client-phone-input" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="notes">Notes</Label>
                <Textarea id="notes" value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} placeholder="Notes internes..." rows={2} data-testid="client-notes-input" />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Annuler</Button>
              <Button type="submit" disabled={submitting} data-testid="save-client-button">
                {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                {editingClient ? 'Mettre à jour' : 'Créer'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Password Dialog */}
      <Dialog open={passwordDialogOpen} onOpenChange={setPasswordDialogOpen}>
        <DialogContent className="sm:max-w-md" data-testid="password-dialog">
          <DialogHeader>
            <DialogTitle>Mot de passe</DialogTitle>
            <DialogDescription>
              {passwordClient?.client_name}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div className="space-y-2">
              <Label>Email de connexion</Label>
              <div className="p-3 bg-muted rounded-lg">
                <code className="text-sm font-mono">{passwordClient?.contact_email}</code>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Mot de passe actuel</Label>
              <div className="flex items-center gap-2 p-3 bg-muted rounded-lg">
                <code className="text-sm font-mono flex-1 break-all" data-testid="current-password-display">
                  {currentPassword
                    ? (showCurrentPwd ? currentPassword : '•'.repeat(Math.max(currentPassword.length, 8)))
                    : <span className="text-muted-foreground italic">Aucun mot de passe lisible enregistré</span>}
                </code>
                {currentPassword && (
                  <>
                    <Button type="button" variant="ghost" size="icon" className="h-7 w-7" onClick={() => setShowCurrentPwd(s => !s)} title={showCurrentPwd ? 'Masquer' : 'Afficher'} data-testid="toggle-current-password">
                      {showCurrentPwd ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </Button>
                    <Button type="button" variant="ghost" size="icon" className="h-7 w-7" onClick={() => { navigator.clipboard?.writeText(currentPassword); toast.success('Copié'); }} title="Copier" data-testid="copy-current-password">
                      <KeyRound className="h-4 w-4" />
                    </Button>
                  </>
                )}
              </div>
            </div>
            {!showChangeMode ? (
              <Button variant="default" className="w-full" onClick={() => setShowChangeMode(true)} data-testid="show-change-password">
                Changer le mot de passe
              </Button>
            ) : (
              <div className="space-y-2">
                <Label htmlFor="new-password">Nouveau mot de passe</Label>
                <div className="relative">
                  <Input
                    id="new-password"
                    type={showPassword ? "text" : "password"}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Entrez le nouveau mot de passe"
                    className="pr-10"
                    data-testid="new-password-input"
                  />
                  <Button type="button" variant="ghost" size="icon" className="absolute right-0 top-0 h-full px-3 hover:bg-transparent" onClick={() => setShowPassword(!showPassword)} data-testid="toggle-new-password">
                    {showPassword ? <EyeOff className="h-4 w-4 text-muted-foreground" /> : <Eye className="h-4 w-4 text-muted-foreground" />}
                  </Button>
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPasswordDialogOpen(false)}>Fermer</Button>
            {showChangeMode && (
              <Button onClick={handleSavePassword} disabled={savingPassword || !newPassword.trim()} data-testid="save-password-button">
                {savingPassword && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Enregistrer
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manage Labs Dialog (multi-labo, max 3) */}
      <Dialog open={labDialogOpen} onOpenChange={setLabDialogOpen}>
        <DialogContent className="sm:max-w-md" data-testid="assign-lab-dialog">
          <DialogHeader>
            <DialogTitle>Gérer les laboratoires</DialogTitle>
            <DialogDescription>
              {labClient ? `Cabinet : ${labClient.client_name}` : ''} — un client peut être lié à 3 laboratoires maximum.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Laboratoires assignés ({labClient?.labs?.length || 0}/3)</Label>
              {labClient?.labs && labClient.labs.length > 0 ? (
                <div className="flex flex-wrap gap-2" data-testid="assigned-labs-list">
                  {labClient.labs.map(l => (
                    <Badge key={l.id} variant="outline" className="status-linked pr-1 pl-3 py-1 flex items-center gap-1">
                      <span>{l.lab_name}</span>
                      <button
                        type="button"
                        className="ml-1 rounded-full hover:bg-destructive/20 p-0.5"
                        onClick={() => handleRemoveLab(l.id)}
                        title="Retirer ce labo"
                        data-testid={`remove-lab-${l.id}`}
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">Aucun laboratoire assigné.</p>
              )}
            </div>

            {(labClient?.labs?.length || 0) < 3 && (
              <div className="space-y-2 border-t pt-4">
                <Label>Ajouter un laboratoire</Label>
                <div className="flex gap-2">
                  <Select value={selectedLabId} onValueChange={setSelectedLabId}>
                    <SelectTrigger data-testid="lab-select" className="flex-1">
                      <SelectValue placeholder="Choisir un laboratoire" />
                    </SelectTrigger>
                    <SelectContent>
                      {labs
                        .filter(l => l.active !== false)
                        .filter(l => !(labClient?.labs || []).some(al => al.id === l.id))
                        .map(l => (
                          <SelectItem key={l.id} value={l.id} data-testid={`lab-option-${l.id}`}>
                            {l.lab_name} {l.contact_email ? `(${l.contact_email})` : ''}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                  <Button onClick={handleAssignLab} disabled={!selectedLabId || savingLab} data-testid="confirm-assign-lab">
                    {savingLab ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Ajouter'}
                  </Button>
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLabDialogOpen(false)}>Fermer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default Clients;
